import { useCallback, useMemo } from "react";
import type { ListRenderItemInfo } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { View, Text } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { useActivityList } from "@src/services/api/hooks";
import type { ActivityItem } from "@src/services/api/activity-api";
import { ActivityItemCard } from "./ActivityItemCard";

interface ActivityListProps {
  siteId?: string;
  cameraKey?: string | null;
}

function ActivityFeedList({ items }: { items: ActivityItem[] }) {
  const { theme } = useUnistyles();

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [items],
  );

  const keyExtractor = useCallback((item: ActivityItem) => item.id, []);

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<ActivityItem>) => (
      <ActivityItemCard item={item} isLast={index === sortedItems.length - 1} />
    ),
    [sortedItems],
  );

  const listHeaderComponent = useMemo(
    () => (
      <View bg="surface" style={{ overflow: "hidden" }}>
        <View
          flexDirection="row"
          alignItems="center"
          px="4xl"
          py="md"
          bg="surface"
          borderBottomWidth={1}
          borderColor="borderCard"
        >
          <View flex={3} pr="md" style={{ minWidth: 0 }}>
            <Text textStyle="xxs/medium" color="textMuted">
              Summary
            </Text>
          </View>
          <View flex={2} pr="md" style={{ minWidth: 0 }}>
            <Text textStyle="xxs/medium" color="textMuted">
              Camera
            </Text>
          </View>
          <View flex={2} pr="md" style={{ minWidth: 0 }}>
            <Text textStyle="xxs/medium" color="textMuted" style={{ textAlign: "right" }}>
              Time
            </Text>
          </View>
          <View width={24} />
        </View>
      </View>
    ),
    [],
  );

  return (
    <View flex={1} bg="background">
      <Animated.FlatList
        data={sortedItems}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={listHeaderComponent}
        removeClippedSubviews={false}
        windowSize={51}
        maxToRenderPerBatch={50}
        initialNumToRender={50}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: theme.spacing["10xl"],
        }}
        showsVerticalScrollIndicator
        scrollIndicatorInsets={{ right: 0, top: 0, bottom: 0, left: 0 }}
      />
    </View>
  );
}

export function ActivityList({ siteId, cameraKey }: ActivityListProps) {
  const { data, isLoading, isFetching, error, refetch } = useActivityList({
    siteId,
    cameraKey: cameraKey ?? undefined,
  });

  return (
    <DataStateView
      // Pass through the raw data from the query so that auth errors
      // (ForbiddenError) are treated as "no data" and can render the
      // centralized forbidden UI instead of an empty state.
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error as Error | null}
      refetch={refetch}
      isEmpty={(items) => items.length === 0}
      loadingMessage="Loading activity..."
      errorTitle="Unable to load activity"
      errorMessage="Please check your connection and try again."
      emptyState={{
        icon: "eventOutline",
        title: "No activity yet",
        message: "Activity events will appear here as they happen.",
      }}
      showStaleDataBanner={false}
    >
      {(items) => <ActivityFeedList items={items} />}
    </DataStateView>
  );
}
